import React from 'react';
import { Dialog, DialogContent } from '@mui/material';
import { MuiButton, MuiCheckbox } from '@ace/ace-ui-web';
import styles from '../registration.module.css';

interface TermsAndConditionsDialogProps {
    open: boolean;
    agreedToTerms: boolean;
    onClose: () => void;
    onAgree: (checked: boolean) => void;
}

const TermsAndConditionsDialog: React.FC<TermsAndConditionsDialogProps> = ({
    open,
    agreedToTerms,
    onClose,
    onAgree
}) => {
    return (
        <Dialog open={open} onClose={onClose}>
            <DialogContent className={styles.maxWLg}>
                <h3 className={styles.modalTitle}>Terms and Conditions</h3>
                <p className={styles.modalDescription}>
                    Please read these terms carefully before signing up.
                </p>
                <div className={styles.modalBody}>
                    <section>
                        <h3 className={styles.termsHeading}>Eligibility and Access</h3>
                        <p className={styles.termsText}>
                            MyAceFlex is intended exclusively for current employees of Ace Hardware Corporation (the <strong>&quot;Company&quot;</strong>) who (i) maintain active employment status and (ii) are assigned to eligible business units, locations, or departments as designated by the Company. Contractors, temporary agency workers, interns, and other third parties may not access MyAceFlex unless they have received the Company&apos;s explicit written authorization.
                        </p>
                        <p className={styles.termsText}>
                            The Company may suspend or revoke access to MyAceFlex at any time, with or without prior notice, including due to changes in employment status, role, eligibility, or for compliance, security, or disciplinary reasons. Eligibility is verified through Workday, and access may be suspended or revoked based on Workday records or other Company determinations.
                        </p>
                        <p className={styles.termsText}>
                            Any written authorization for contractors, temporary agency workers, interns, or other third parties must: (i) be signed by an authorized Company representative; (ii) specify the scope of permitted access and the duration of the authorization; and (iii) state that the authorization may be revoked by the Company at any time.
                        </p>
                    </section>

                    <section>
                        <h3 className={styles.termsHeading}>Participation; Accounts; Security</h3>
                        <p className={styles.termsText}>
                            Participation in MyAceFlex is voluntary. Users are responsible for setting up and maintaining their accounts using assigned corporate credentials or other Company-approved authentication methods. Users must keep all personal and employment-related information accurate and current. Users may submit information or requests only for themselves and only for shifts they are qualified to perform at locations for which they are credentialed.
                        </p>
                        <p className={styles.termsText}>
                            Users must comply with all Company policies, including the Enterprise Technology Policy. Users must safeguard login credentials and devices, must not share access with any other person, and must ensure that all information submitted through MyAceFlex (including availability, shift details, and time-off requests) is accurate and not misleading. Users must promptly report suspected credential compromise, lost or stolen devices, or suspected unauthorized access to IT Security and HR.
                        </p>
                    </section>

                    <section>
                        <h3 className={styles.termsHeading}>Pay; Timekeeping; Voluntary Time Off (VTO)</h3>
                        <p className={styles.termsText}>
                            A user&apos;s rate of pay and any applicable differentials are determined by the user&apos;s classification and applicable Company policies and shift rules. MyAceFlex does not replace or alter the Company&apos;s pay or timekeeping policies or practices.
                        </p>
                        <p className={styles.termsText}>
                            The Company&apos;s official timekeeping system is the exclusive record of compensable time. MyAceFlex does not authorize off-the-clock work. All work time must be recorded in accordance with Company policy. Overtime, shift differentials, premiums, and location-based pay apply only as provided in Company policy and applicable law and, where required, must be preapproved by management.
                        </p>
                        <p className={styles.termsText}>
                            Unless otherwise specified in writing, <strong>Voluntary Time Off (&quot;VTO&quot;)</strong> is unpaid, employee-elected time away from scheduled work and may affect accruals, eligibility, or benefits in accordance with Company policy and applicable law. VTO does not waive or limit any rights to protected leave or scheduling protections. If there is a conflict, applicable law or an applicable collective bargaining agreement controls.
                        </p>
                    </section>

                    <section>
                        <h3 className={styles.termsHeading}>Requests; Approvals; Misuse</h3>
                        <p className={styles.termsText}>
                            Submitting information or requests through MyAceFlex does not guarantee approval or fulfillment. Approvals are discretionary and may be based on business needs, staffing levels, skills or qualifications.
                        </p>
                        <p className={styles.termsText}>
                            Requests must be submitted by the published cutoff times. All entries are subject to Company review and confirmation. Users are responsible for monitoring MyAceFlex notifications and schedules and promptly addressing discrepancies.
                        </p>
                        <p className={styles.termsText}>
                            Misuse of MyAceFlex, including unauthorized access, submitting information on behalf of another person, falsification of records, or interference with MyAceFlex functionality, may result in suspension of access, disciplinary action up to and including termination, and other actions permitted by Company policy and applicable law.
                        </p>
                    </section>

                    <section>
                        <h3 className={styles.termsHeading}>Accessibility; Non-Discrimination; Accommodations</h3>
                        <p className={styles.termsText}>
                            Access to shift opportunities, swaps, and VTO will be administered in a nondiscriminatory manner consistent with Company policy and applicable law. Employees who need a disability or religious accommodation relating to MyAceFlex use or scheduling should contact HR. Where required, the Company will provide an alternative, effective means of access.
                        </p>
                    </section>

                    <section>
                        <h3 className={styles.termsHeading}>Records; Changes to Terms</h3>
                        <p className={styles.termsText}>
                            Records will be retained in accordance with Company retention schedules. Upon separation from employment, access to MyAceFlex will terminate, and data will be retained or deleted in accordance with Company policy and applicable law.
                        </p>
                        <p className={styles.termsText}>
                            The Company may modify these Terms from time to time. Material changes will be communicated through MyAceFlex or other Company channels and will be effective upon posting unless a later effective date is stated. Continued use of MyAceFlex after the effective date constitutes acceptance of the modified Terms.
                        </p>
                    </section>

                    <section>
                        <h3 className={styles.termsHeading}>No Contract of Employment</h3>
                        <p className={styles.termsText}>
                            These Terms do not create a contract of employment for any definite term and do not alter at-will status where applicable.
                        </p>
                    </section>

                    <section className={styles.acceptanceSection}>
                        <h3 className={styles.termsHeading}>Acceptance</h3>
                        <p className={styles.termsText}>
                            By selecting <strong>&quot;I Agree,&quot;</strong> accessing, or using MyAceFlex, you: (a) acknowledge that you have read and understand these Terms; (b) agree to be bound by them; and (c) consent to the collection and use of your data as described. If you do not agree, do not access or use MyAceFlex.
                        </p>
                        <div className={styles.checkboxWrapper}>
                            <MuiCheckbox
                                color="secondary"
                                checked={agreedToTerms}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    onAgree(event.target.checked);
                                }}
                            />
                            <span className={styles.termsLinkText}>I Agree</span>
                        </div>
                    </section>
                </div>
                <div className={styles.modalFooter}>
                    <MuiButton onClick={onClose} sx={{ width: '100%' }}>
                        Close
                    </MuiButton>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default TermsAndConditionsDialog;